import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Plane, AlertTriangle, Globe } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function LoginPage({ onSwitchToRegister }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #020617 100%)' }}>
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1769697694263-c8c4b264392f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxhaXJwb3J0JTIwY29udHJvbCUyMHRvd2VyJTIwbmlnaHR8ZW58MHx8fHwxNzcwNzQ2MjA2fDA&ixlib=rb-4.1.0&q=85)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <BrandLogo variant="full" className="w-64 mb-8" data-testid="login-brand-logo" />
          
          <h2 className="text-4xl font-bold text-white mb-6 font-barlow leading-tight">
            {t('auth.welcome')}
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-md">
            {t('auth.subtitle')}
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded bg-slate-800/50 flex items-center justify-center">
                <Plane className="w-4 h-4 text-sky-400" />
              </div>
              <span>{t('auth.icaoCompliant')}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded bg-slate-800/50 flex items-center justify-center">
                <Shield className="w-4 h-4 text-sky-400" />
              </div>
              <span>{t('auth.gacaEasaAligned')}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded bg-slate-800/50 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-sky-400" />
              </div>
              <span>{t('auth.aiPoweredRiskAnalysis')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language toggle */}
          <div className="flex justify-end mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage(language === 'en' ? 'ar' : 'en')}
              className="text-slate-400 hover:text-white"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'en' ? 'العربية' : 'English'}
            </Button>
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <BrandLogo variant="full" className="w-48" />
          </div>

          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-md">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-white font-barlow">
                {t('auth.loginTitle')}
              </CardTitle>
              <CardDescription className="text-slate-400">
                {t('auth.loginDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-sm bg-red-900/20 border border-red-900/50 text-red-400 text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@aviation.com"
                    required
                    className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600 focus:ring-sky-600/20"
                    data-testid="email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-600 focus:ring-sky-600/20"
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white font-medium py-2.5 rounded-sm transition-colors"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('auth.loginButton')
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <span className="text-slate-400 text-sm">{t('auth.noAccount')} </span>
                <button
                  onClick={onSwitchToRegister}
                  className="text-sky-400 hover:text-sky-300 text-sm font-medium transition-colors"
                  data-testid="switch-to-register"
                >
                  {t('common.register')}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-slate-500 text-xs mt-6">
            {t('auth.copyright', { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center justify-center gap-3 text-slate-500 text-xs mt-2" data-testid="login-legal-links">
            <a href="#privacy"          className="hover:text-cyan-300 transition-colors">Privacy</a>
            <span className="text-slate-700">·</span>
            <a href="#terms"            className="hover:text-cyan-300 transition-colors">Terms</a>
            <span className="text-slate-700">·</span>
            <a href="#data-processing"  className="hover:text-cyan-300 transition-colors">Data Processing</a>
          </div>
        </div>
      </div>
    </div>
  );
}
